import type { SessionState } from '../domain/session.ts';

/**
 * RULE DCC-SESS-001 - Rules for Survival p.12.
 * On the Tutorial Floors (1-2), record each Skill attempted untrained. At the
 * end of a 2-hour session the crawler gains 1 Rank in ONE of those Skills, and
 * the list is cleared. Nothing carries over to a later session.
 *
 * The choice of which Skill belongs to the player, so this module records
 * candidates and never spends the choice automatically.
 */
export const TUTORIAL_FLOORS: readonly number[] = [1, 2];

export function isTutorialFloor(floor: number): boolean {
  return TUTORIAL_FLOORS.includes(floor);
}

export function recordUntrainedAttempt(
  session: SessionState,
  skillId: string,
  floor: number,
): SessionState {
  if (!isTutorialFloor(floor)) return session;
  if (session.untrainedSkillsAttempted.includes(skillId)) return session;
  return {
    ...session,
    untrainedSkillsAttempted: [...session.untrainedSkillsAttempted, skillId],
  };
}

/** The Skills the player may choose between at the end of the session. */
export function endOfSessionRankCandidates(session: SessionState): string[] {
  return [...session.untrainedSkillsAttempted];
}

export function clearUntrainedAttempts(session: SessionState): SessionState {
  return { ...session, untrainedSkillsAttempted: [] };
}

/**
 * Applying the end-of-session award.
 *
 * RULE DCC-SESS-002 - Rules for Survival p.12.
 * At the end of a 2-hour session on the Tutorial Floors, the crawler gains 1
 * Rank in ONE of the Skills attempted untrained that session. The remaining
 * candidates are then cleared and cannot carry over.
 *
 * This is deliberately SEPARATE from ordinary Skill Advancement Checks
 * (C&C p.42), which are a different mechanic with their own roll. Nothing here
 * should ever be reused for those.
 *
 * The choice is never made automatically: a caller must name the Skill.
 */
export const UNTRAINED_AWARD_RANK = 1;

export type UntrainedAwardOutcome =
  | { ok: true; skills: CharacterSkillEntry[]; session: SessionState }
  | { ok: false; reason: string };

/** The minimum shape this module needs from a Character. */
export interface CharacterSkillEntry {
  skillId: string;
  rank: number;
}

export interface UntrainedAwardInput {
  skills: readonly CharacterSkillEntry[];
  floor: number;
  session: SessionState;
  chosenSkillId: string;
}

export function applyUntrainedAward(input: UntrainedAwardInput): UntrainedAwardOutcome {
  const { session } = input;
  const awarded = awardUntrainedRank({
    skills: input.skills,
    floor: input.floor,
    candidates: endOfSessionRankCandidates(session),
    chosenSkillId: input.chosenSkillId,
  });
  if (!awarded.ok) return awarded;
  return { ok: true, skills: awarded.skills, session: clearUntrainedAttempts(session) };
}

export type AwardRankOutcome =
  | { ok: true; skills: CharacterSkillEntry[] }
  | { ok: false; reason: string };

/**
 * The award itself, decoupled from where the candidates came from.
 *
 * Two callers need it: the 2-hour checkpoint, whose candidates are a snapshot
 * of the interval that just closed, and End Session, whose candidates are
 * whatever is still sitting in the session.
 */
export function awardUntrainedRank(input: {
  skills: readonly CharacterSkillEntry[];
  floor: number;
  candidates: readonly string[];
  chosenSkillId: string;
}): AwardRankOutcome {
  const { skills, floor, candidates, chosenSkillId } = input;

  if (!isTutorialFloor(floor)) {
    return {
      ok: false,
      reason: `The end-of-session untrained Skill award only applies on the Tutorial Floors (${TUTORIAL_FLOORS.join(' and ')}). This crawler is on Floor ${floor}.`,
    };
  }

  if (candidates.length === 0) {
    return { ok: false, reason: 'No Skills were attempted untrained this session.' };
  }
  if (!candidates.includes(chosenSkillId)) {
    return {
      ok: false,
      reason: `"${chosenSkillId}" was not attempted untrained this session, so it cannot be awarded a Rank.`,
    };
  }
  if (skills.some((s) => s.skillId === chosenSkillId)) {
    return {
      ok: false,
      reason: `The crawler already has Ranks in "${chosenSkillId}", so it was not an untrained attempt.`,
    };
  }

  return { ok: true, skills: [...skills, { skillId: chosenSkillId, rank: UNTRAINED_AWARD_RANK }] };
}

/**
 * Throwing the candidates away without taking a Rank. Only ever reached through
 * an explicit destructive confirmation in the interface; the ordinary end of a
 * session must not silently discard eligible candidates.
 */
export function discardUntrainedCandidates(session: SessionState): SessionState {
  return clearUntrainedAttempts(session);
}

/** True when ending the session would throw away an unclaimed Rank. */
export function hasUnclaimedAward(session: SessionState, floor: number): boolean {
  return isTutorialFloor(floor) && endOfSessionRankCandidates(session).length > 0;
}
